import { createEffect, createSignal, onCleanup } from "solid-js"
import { requireBillingOpenAnalystApiAccessToken } from "@/services/billing-auth"
import { openAnalystBillingApiBaseUrl } from "@/utils/openanalyst-hosts"

const BILLING_API_BASE_URL = openAnalystBillingApiBaseUrl()

const REFRESH_INTERVAL_MS = 60_000
let configuredFetch: typeof fetch | null = null

export function configureCreditBalanceFetch(fetcher: typeof fetch | null) {
  configuredFetch = fetcher
}

export type CreditBalance = {
  plan: {
    name: string
    limit: number
    used: number
    remaining: number
  }
  purchases: {
    count: number
    totalCredits: number
    totalRemaining: number
  }
  total: {
    available: number
    expiringSoon: {
      credits: number
      date: string
      source: string
    } | null
  }
}

type BalanceResponse = {
  success: boolean
  balance?: CreditBalance
  error?: {
    code: string
    message: string
  }
}

export async function fetchCreditBalance(): Promise<CreditBalance | undefined> {
  const response = await (configuredFetch ?? fetch)(`${BILLING_API_BASE_URL}/credits/balance`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await requireBillingOpenAnalystApiAccessToken()}`,
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    // Surface the backend's real reason instead of a silent `undefined`, so the
    // UI can tell a server-side billing fault apart from "offline / no balance".
    // The OpenAnalyst billing API returns { error, message } on failures — e.g.
    // `billing_setup_failed` when quota provisioning errors on the server.
    const detail = (await response.json().catch(() => undefined)) as
      | { error?: string; message?: string }
      | undefined
    if (detail?.error === "billing_setup_failed") {
      throw new Error("Server billing isn't ready (quota provisioning failed). Retry shortly or contact support.")
    }
    throw new Error(detail?.message || detail?.error || `Couldn't reach billing (HTTP ${response.status}).`)
  }

  const data = (await response.json().catch(() => undefined)) as BalanceResponse | undefined
  if (!data?.success || !data.balance) return undefined

  return data.balance
}

export function useCredits() {
  const [balance, setBalance] = createSignal<CreditBalance | undefined>(undefined)
  const [loading, setLoading] = createSignal(false)
  const [error, setError] = createSignal<string | undefined>(undefined)

  const refresh = async () => {
    setLoading(true)
    setError(undefined)
    try {
      const result = await fetchCreditBalance()
      setBalance(result)
      if (!result) setError("Could not load balance")
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause)
      // Map the known "not signed in" guard to a friendly prompt; otherwise show
      // the backend's actual reason (e.g. server billing/quota failure) instead
      // of a generic message that hides a real outage.
      setError(message.includes("missing_openanalyst_session") ? "Sign in to view your balance." : message || "Could not load balance")
    } finally {
      setLoading(false)
    }
  }

  createEffect(() => {
    void refresh()

    const interval = setInterval(() => void refresh(), REFRESH_INTERVAL_MS)
    onCleanup(() => clearInterval(interval))
  })

  return {
    balance,
    loading,
    error,
    refresh,
    available: () => balance()?.total.available,
  }
}
